import { useEffect, type RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Scroll-driven motion for the v2 landing page.
 *
 * Conventions (set in JSX, styled-safe without JS):
 * - [data-reveal]            fades/rises in once when scrolled into view; optional
 *                            [data-reveal-delay="0.1"] in seconds.
 * - [data-reveal-group]      staggers its direct children in.
 * - [data-parallax="0.12"]   drifts vertically while its [data-parallax-scope]
 *                            ancestor (or itself) crosses the viewport; the value
 *                            is the drift strength, negative inverts direction.
 * - .v2-note-line            typed in sequentially when the scribe screen enters.
 *
 * Honors prefers-reduced-motion by skipping everything (content stays visible
 * because initial states are applied by GSAP, not CSS).
 */
export function useLandingV2Motion(rootRef: RefObject<HTMLElement>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    root.classList.add('v2-js');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      root.classList.add('v2-reduced');
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((el) => {
        gsap.fromTo(
          el,
          { y: 42, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1.05,
            delay: parseFloat(el.dataset.revealDelay ?? '0'),
            ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 86%', once: true },
          },
        );
      });

      gsap.utils.toArray<HTMLElement>('[data-reveal-group]').forEach((group) => {
        gsap.fromTo(
          Array.from(group.children),
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.85,
            ease: 'power3.out',
            stagger: 0.09,
            scrollTrigger: { trigger: group, start: 'top 86%', once: true },
          },
        );
      });

      gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.dataset.parallax ?? '0.12');
        gsap.fromTo(
          el,
          { y: speed * 140 },
          {
            y: speed * -140,
            ease: 'none',
            scrollTrigger: {
              trigger: el.closest<HTMLElement>('[data-parallax-scope]') ?? el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.6,
            },
          },
        );
      });

      const noteLines = gsap.utils.toArray<HTMLElement>('.v2-note-line');
      if (noteLines.length) {
        gsap.fromTo(
          noteLines,
          { opacity: 0, y: 12 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: 'power2.out',
            stagger: 0.55,
            scrollTrigger: { trigger: '.v2-scribe-screen', start: 'top 72%', once: true },
          },
        );
      }
    }, root);

    return () => ctx.revert();
  }, [rootRef]);
}
